import { compact } from 'lodash';
import { useMemo } from 'react';
import { useRouteMatch } from 'react-router';
import { usePrevious, useUpdateEffect } from 'react-use';

import { VisitDocumentTemplateType } from '@dreryk/egabinet-visit-frontend-api';

import { PromiseVoidFunction } from '@dreryk/react-common';
import { useDraftCqrsSearch } from '@dreryk/react-draft';

import { visitPaths } from '~libs.desktop.shared.paths';

import { getDocumentTemplateDraftTag, getTemplateDraftId } from '../../visit.draft';

import { getDocumentTemplateType } from './DocumentTemplateAction.utils';

type UseDocumentTemplateRouteMatchOptions = {
   onLeave: VoidFunction;
};

export const useDocumentTemplateRouteMatch = (options: UseDocumentTemplateRouteMatchOptions): void => {
   const documentTemplateRouteMatch = useRouteMatch({
      path: [
         visitPaths.REFERRAL_TO_HOSPITAL_PATH,
         visitPaths.REFERRAL_TO_PSYCHIATRIC_HOSPITAL_PATH,
         visitPaths.REFERRAL_TO_DIAGNOSTIC_EXAMINATION_PATH,
         visitPaths.REFERRAL_TO_ANALYTICAL_EXAMINATION_PATH,
         visitPaths.REFERRAL_TO_SPECIALIST_PATH,
         visitPaths.REFERRAL_TO_REHABILITATION_PATH,
         visitPaths.MEDICAL_CERTIFICATE_PATH,
         visitPaths.DOCUMENT_TEMPLATE_PATH,
      ],
   });

   const previousDocumentTemplateRouteMatch = usePrevious(documentTemplateRouteMatch);

   useUpdateEffect(() => {
      if (previousDocumentTemplateRouteMatch?.url !== documentTemplateRouteMatch?.url) {
         options.onLeave();
      }
   }, [documentTemplateRouteMatch]);
};

type UseDocumentTemplateDraftQueryReturnFn = {
   priorityTemplateTypes: VisitDocumentTemplateType[] | undefined;
   priorityTemplateIds: number[] | undefined;
   refetchDocumentTemplateDrafts: PromiseVoidFunction;
};

export const useDocumentTemplateDraftQuery = (visitId: number): UseDocumentTemplateDraftQueryReturnFn => {
   const { data: documentTemplateDrafts, refetch: refetchDocumentTemplateDrafts } = useDraftCqrsSearch({
      tag: getDocumentTemplateDraftTag(visitId),
   });

   const priorityTemplateTypes = useMemo<VisitDocumentTemplateType[] | undefined>(() => {
      return compact(documentTemplateDrafts?.map((draft) => getDocumentTemplateType(draft.token)));
   }, [documentTemplateDrafts]);

   const priorityTemplateIds = useMemo<number[] | undefined>(() => {
      return compact(documentTemplateDrafts?.map((draft) => getTemplateDraftId(draft.token)));
   }, [documentTemplateDrafts]);

   return {
      priorityTemplateTypes: priorityTemplateTypes,
      priorityTemplateIds: priorityTemplateIds,
      refetchDocumentTemplateDrafts: refetchDocumentTemplateDrafts,
   };
};
